import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, cfmt } from '../lib/api';
import { TID } from '../constants/testIds';
import NeighbourhoodCard from '../components/common/NeighbourhoodCard';
import { ArrowRight, ArrowLeft } from 'lucide-react';

const LIFESTYLE = [
    { k: 'walkable', l: 'Walk everywhere' },
    { k: 'family', l: 'Family-friendly' },
    { k: 'schools', l: 'Top schools' },
    { k: 'safe', l: 'Very safe' },
    { k: 'transit', l: 'GO train access' },
    { k: 'green', l: 'Parks & trails' },
    { k: 'retiree', l: 'Retiree-friendly' },
    { k: 'diverse', l: 'Diverse community' },
    { k: 'small-town', l: 'Small-town feel' },
    { k: 'waterfront', l: 'Waterfront' },
    { k: 'heritage', l: 'Heritage character' },
    { k: 'affordable', l: 'Under $800k' },
    { k: 'investment', l: 'Investment upside' },
];

const CITIES = [
    'Any city',
    'Whitby', 'Oshawa', 'Pickering', 'Ajax',
    'Hamilton', 'Burlington', 'Oakville', 'Milton', 'Halton Hills',
    'Brantford', 'Paris', 'St. George',
    'Niagara-on-the-Lake', 'St. Catharines', 'Grimsby', 'Welland', 'Pelham',
];

export default function Quiz() {
    const [step, setStep] = useState(0);
    const [picks, setPicks] = useState([]);
    const [budget, setBudget] = useState(900000);
    const [city, setCity] = useState('Any city');
    const [commute, setCommute] = useState(30);

    const match = useMutation({
        mutationFn: async () => (await api.post('/quiz/match', {
            lifestyle: picks,
            budget_max: budget,
            city_preference: city === 'Any city' ? null : city,
            commute_max_min: commute,
        })).data,
    });

    const toggle = (k) => setPicks((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);

    const stepper = ['Lifestyle', 'Budget', 'Location', 'Results'];

    return (
        <div>
            <section className="border-b border-neutral-200">
                <div className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-14 pb-8">
                    <div className="overline">Lifestyle match</div>
                    <h1 className="font-editorial text-[42px] lg:text-[58px] tracking-editorial mt-4 leading-[1.05]">
                        Tell us what you value. We'll show you where to live.
                    </h1>
                </div>
                <div className="max-w-[1100px] mx-auto px-6 lg:px-10 pb-6 flex items-center gap-4 text-[12px] uppercase tracking-[0.18em]">
                    {stepper.map((s, i) => (
                        <div key={s} className={`flex items-center gap-2 ${i === step ? 'text-black' : 'text-neutral-400'}`}>
                            <span className="font-mono-data">{String(i + 1).padStart(2, '0')}</span>
                            <span>{s}</span>
                            {i < stepper.length - 1 && <span className="w-6 h-px bg-neutral-300 ml-2" />}
                        </div>
                    ))}
                </div>
            </section>

            <section>
                <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-12" data-testid={TID.quiz.step}>
                    {step === 0 && (
                        <div>
                            <div className="font-editorial text-[26px] tracking-editorial max-w-2xl">
                                Pick everything that matters. There&apos;s no right answer — only your answer.
                            </div>
                            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                {LIFESTYLE.map((o) => {
                                    const active = picks.includes(o.k);
                                    return (
                                        <button
                                            key={o.k}
                                            data-testid={`${TID.quiz.chip}-${o.k}`}
                                            onClick={() => toggle(o.k)}
                                            className={`text-left px-4 py-3 border transition-colors text-[14px] ${active ? 'bg-brand-navy text-white border-black' : 'bg-white border-neutral-300 hover:border-brand-navy'}`}
                                           
                                        >
                                            {o.l}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {step === 1 && (
                        <div className="max-w-2xl">
                            <div className="font-editorial text-[26px] tracking-editorial">What&apos;s your top-end home price?</div>
                            <div className="mt-8">
                                <div className="font-mono-data text-[42px] tabular text-[#2F6B47]">{cfmt(budget)}</div>
                                <input
                                    data-testid={TID.quiz.budget}
                                    type="range"
                                    min={300000} max={3000000} step={25000}
                                    value={budget}
                                    onChange={(e) => setBudget(Number(e.target.value))}
                                    className="w-full mt-6"
                                />
                                <div className="flex justify-between text-[11px] text-neutral-500 font-mono-data mt-2">
                                    <span>$300k</span><span>$3.0M</span>
                                </div>
                            </div>
                            <div className="mt-10">
                                <label className="overline">Max commute to downtown (minutes)</label>
                                <input
                                    type="range" min={10} max={60} step={5}
                                    value={commute}
                                    onChange={(e) => setCommute(Number(e.target.value))}
                                    className="w-full mt-4"
                                />
                                <div className="font-mono-data text-[18px] tabular mt-2">{commute} min</div>
                            </div>
                        </div>
                    )}
                    {step === 2 && (
                        <div className="max-w-xl">
                            <div className="font-editorial text-[26px] tracking-editorial">Any city preference?</div>
                            {/* keep clean */}
                            <select
                                data-testid={TID.quiz.city}
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                className="mt-8 w-full border border-neutral-300 px-4 py-4 text-[16px] bg-white"
                               
                            >
                                {CITIES.map((c) => <option key={c}>{c}</option>)}
                            </select>
                        </div>
                    )}
                    {step === 3 && (
                        <div data-testid={TID.quiz.result}>
                            {match.isPending && <div className="text-neutral-500">Analysing…</div>}
                            {match.data && (
                                <div>
                                    <div className="overline">Your top matches</div>
                                    <div className="font-editorial text-[32px] tracking-editorial mt-3">Six neighbourhoods that fit.</div>
                                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {match.data.matches.map((n) => (
                                            <div key={n.slug} className="border border-neutral-200">
                                                <NeighbourhoodCard n={n} sub={`Match score: ${n.match_score}`} />
                                                {n.rationale && (
                                                    <div className="p-5 border-t border-neutral-200 bg-[#F7F7F5]">
                                                        <div className="overline text-[#2F6B47]">Why it fits</div>
                                                        <div className="mt-2 text-[13.5px] leading-relaxed text-neutral-700">{n.rationale}</div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="mt-12 flex items-center justify-between">
                        <button
                            data-testid={TID.quiz.back}
                            disabled={step === 0}
                            onClick={() => setStep((s) => Math.max(0, s - 1))}
                            className="inline-flex items-center gap-2 text-[13.5px] text-neutral-500 disabled:opacity-40"
                        >
                            <ArrowLeft className="w-4 h-4" /> Back
                        </button>
                        {step < 3 ? (
                            <button
                                data-testid={TID.quiz.next}
                                onClick={() => {
                                    if (step === 2) match.mutate();
                                    setStep((s) => Math.min(3, s + 1));
                                }}
                                disabled={step === 0 && picks.length === 0}
                                className="inline-flex items-center gap-2 bg-brand-navy text-white px-6 py-3 text-[13.5px] hover-invert hover:bg-brand-gold hover:text-brand-navy border border-brand-navy disabled:opacity-50"
                               
                            >
                                {step === 2 ? 'See matches' : 'Continue'}
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        ) : (
                            <button
                                data-testid={TID.quiz.finish}
                                onClick={() => { setStep(0); }}
                                className="inline-flex items-center gap-2 border border-neutral-300 px-6 py-3 text-[13.5px] hover:border-brand-navy"
                               
                            >
                                Start over
                            </button>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
